import type { EstadoLicenca } from './licencaApi';
import { Button } from '@/shared/ui/Button';

type Props = {
  estado: EstadoLicenca;
  recheck: () => void;
  carregando: boolean;
  erro: string | null;
  exportarDados: () => void;
  onSair: () => void | Promise<void>;
};

// O backend marca toda licença bloqueada sem observação como "bloqueada
// pelo administrador" — texto técnico que o usuário final não precisa ver.
// Filtramos para cair no motivo genérico ("entre em contato com o suporte").
const MOTIVO_GENERICO_ADMIN = /bloqueada pelo administrador/i;

function formatarData(iso: string | null): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('pt-BR');
  } catch {
    return iso;
  }
}

export function BloqueioLicenca({
  estado,
  recheck,
  carregando,
  erro,
  exportarDados,
  onSair,
}: Props) {
  const titulo = estado.status === 'sem-acesso' ? 'Licença não verificada' : 'Assinatura bloqueada';
  const motivoBruto =
    estado.motivoBloqueio && !MOTIVO_GENERICO_ADMIN.test(estado.motivoBloqueio)
      ? estado.motivoBloqueio
      : null;
  const motivo =
    motivoBruto ||
    'Esta máquina não possui assinatura ativa. Entre em contato com o suporte para regularizar.';

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="lic-bloq-titulo"
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(15, 23, 42, 0.92)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 9999,
        padding: 24,
      }}
    >
      <div
        style={{
          background: '#fff',
          borderRadius: 12,
          padding: 32,
          maxWidth: 520,
          width: '100%',
          boxShadow: '0 25px 60px rgba(0,0,0,0.5)',
        }}
      >
        <div
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 56,
            height: 56,
            borderRadius: '50%',
            background: '#fee2e2',
            color: '#b91c1c',
            fontSize: 28,
            fontWeight: 700,
            marginBottom: 16,
          }}
          aria-hidden="true"
        >
          !
        </div>
        <h2 id="lic-bloq-titulo" style={{ margin: '0 0 8px', fontSize: 22, color: '#0f172a' }}>
          {titulo}
        </h2>
        <p style={{ margin: '0 0 16px', color: '#475569', lineHeight: 1.5 }}>{motivo}</p>

        <div
          style={{
            background: '#f1f5f9',
            borderRadius: 8,
            padding: '12px 14px',
            fontSize: 13,
            color: '#334155',
            marginBottom: 20,
          }}
        >
          <strong>Como regularizar:</strong>
          <ol style={{ margin: '6px 0 0 18px', padding: 0, lineHeight: 1.6 }}>
            <li>Entre em contato com o suporte Oficina Ágil pelo WhatsApp ou e-mail.</li>
            <li>Após confirmação do pagamento, sua licença é reativada em minutos.</li>
            <li>Clique em <strong>Verificar novamente</strong> abaixo para retomar o uso.</li>
          </ol>
        </div>

        {erro && (
          <div
            style={{
              background: '#fef2f2',
              border: '1px solid #fecaca',
              color: '#991b1b',
              padding: '10px 12px',
              borderRadius: 8,
              fontSize: 12,
              marginBottom: 14,
              wordBreak: 'break-word',
            }}
          >
            {erro}
          </div>
        )}

        <div
          style={{
            display: 'flex',
            gap: 8,
            flexWrap: 'wrap',
            justifyContent: 'flex-end',
          }}
        >
          <Button onClick={onSair} disabled={carregando}>
            Sair da conta
          </Button>
          <Button onClick={exportarDados} disabled={carregando}>
            Exportar meus dados
          </Button>
          <Button variante="primary" onClick={recheck} disabled={carregando}>
            {carregando ? 'Verificando…' : 'Verificar novamente'}
          </Button>
        </div>

        <details style={{ marginTop: 16, fontSize: 11, color: '#64748b' }}>
          <summary style={{ cursor: 'pointer', userSelect: 'none' }}>
            Detalhes técnicos
          </summary>
          <div style={{ marginTop: 8, lineHeight: 1.6 }}>
            <div>
              <strong>Origem do estado:</strong> {estado.origem}
            </div>
            {estado.validaAte && (
              <div>
                <strong>Válida até:</strong> {formatarData(estado.validaAte)}
              </div>
            )}
            {estado.ultimoCheckEm && (
              <div>
                <strong>Último contato:</strong> {formatarData(estado.ultimoCheckEm)}
              </div>
            )}
            {estado.ultimoErro && (
              <div style={{ wordBreak: 'break-word' }}>
                <strong>Último erro do servidor:</strong> {estado.ultimoErro}
              </div>
            )}
          </div>
        </details>
      </div>
    </div>
  );
}

type AvisoProps = {
  estado: EstadoLicenca;
};

export function AvisoLicenca({ estado }: AvisoProps) {
  const dias = estado.validaAte
    ? Math.ceil((new Date(estado.validaAte).getTime() - Date.now()) / 86400_000)
    : null;

  // Mostra banner em três cenários: trial, vencida, e assinatura ativa
  // perto do vencimento (≤5 dias) sem renovação automática. Quando o MP
  // renova sozinho (cartão recorrente), suprimimos o aviso — não há nada
  // que o usuário precise fazer.
  const ativaPertoDoVencimento =
    estado.status === 'ativa' &&
    !estado.renovacaoAutomatica &&
    dias !== null &&
    dias <= 5 &&
    dias > 0;

  if (
    estado.status !== 'vencida' &&
    estado.status !== 'trial' &&
    !ativaPertoDoVencimento
  ) {
    return null;
  }

  let mensagem = '';
  let cor = '#fbbf24';
  if (estado.status === 'trial') {
    mensagem =
      dias !== null && dias > 0
        ? `Período de avaliação: ${dias} dia${dias === 1 ? '' : 's'} restante${dias === 1 ? '' : 's'}.`
        : 'Período de avaliação termina em breve.';
    cor = '#3b82f6';
  } else if (estado.status === 'vencida') {
    mensagem =
      'Assinatura vencida. Renove para evitar bloqueio do sistema nos próximos dias.';
    cor = '#f59e0b';
  } else if (ativaPertoDoVencimento) {
    mensagem =
      dias === 1
        ? 'Sua assinatura vence amanhã. Renove agora para não perder o acesso.'
        : `Sua assinatura vence em ${dias} dias. Renove para manter o acesso.`;
    cor = '#fbbf24';
  }

  return (
    <div
      role="status"
      style={{
        background: cor,
        color: '#0f172a',
        padding: '8px 16px',
        fontSize: 13,
        fontWeight: 500,
        textAlign: 'center',
      }}
    >
      {mensagem}
    </div>
  );
}
